/**
 * /models — 车型评分列表（Phase 4 §4.2）。
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';

import ChipFilter from '@/components/ChipFilter';
import DataTable from '@/components/DataTable';
import MockBanner from '@/components/MockBanner';
import Panel from '@/components/Panel';
import RiskTag from '@/components/RiskTag';
import TrendArrow from '@/components/TrendArrow';
import { api } from '@/lib/api';
import {
  DATA_NOT_READY_HINT,
  isWeekDataReady,
  modelsFromEnvelope,
  type ModelsWeeklyEnvelope,
  type WeekStatus,
} from '@/lib/weekData';
import { useWeekStore } from '@/stores/weekStore';

interface ModelRow {
  model_id: string;
  brand_id: string;
  model_name: string;
  segment: string | null;
  powertrain_type: string | null;
  week: string;
  metrics: {
    avg_score: number | null;
    battery_score: number | null;
    cabin_score: number | null;
    driving_score: number | null;
    comfort_score: number | null;
    service_score: number | null;
    smart_driving_score: number | null;
    review_count: number;
    complaint_count: number;
    complaint_count_wow: number | null;
    risk_score: number;
    risk_level: '低' | '中' | '高' | '重大';
  } | null;
}

const BRAND_OPTIONS = [
  { value: '', label: '全部品牌' },
  { value: 'GAC-AION', label: '广汽埃安' },
  { value: 'GAC-TRUMP', label: '广汽传祺' },
  { value: 'GAC-HYPER', label: '昊铂' },
  { value: 'GAC-HONDA', label: '广汽本田' },
  { value: 'GAC-TOYOTA', label: '广汽丰田' },
];

const POWERTRAIN_OPTIONS = [
  { value: '', label: '全部动力' },
  { value: 'BEV', label: '纯电' },
  { value: 'PHEV', label: '插混' },
  { value: 'ICE', label: '燃油' },
];

function scoreColor(s: number | null | undefined): string {
  if (s === null || s === undefined) return 'text-slate-400';
  if (s >= 4.0) return 'text-risk-low';
  if (s >= 3.5) return 'text-amber-600';
  if (s >= 3.0) return 'text-risk-high';
  return 'text-risk-major';
}

function ScoreCell({ value }: { value: number | null | undefined }) {
  return (
    <span className={`font-semibold ${scoreColor(value)}`}>
      {value !== null && value !== undefined ? value.toFixed(2) : '—'}
    </span>
  );
}

export default function ModelsPage() {
  const { week } = useWeekStore();
  const [brandId, setBrandId] = useState('');
  const [powertrain, setPowertrain] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['modelsWeekly', week, brandId, powertrain],
    queryFn: async () => {
      const params: Record<string, string> = { week };
      if (brandId) params.brand_id = brandId;
      // PR 9.2m: envelope {week, week_status, models}(旧 array 作废)
      const res = await api.get<ModelsWeeklyEnvelope<ModelRow>>('/models/weekly', { params });
      let rows = modelsFromEnvelope(res.data);
      if (powertrain) rows = rows.filter((r) => r.powertrain_type === powertrain);
      return { week_status: res.data.week_status, rows };
    },
    retry: false,
  });

  // PR 9.2m: not-ready 周(W23 红线)→ 不显硬 0/风险分,改 placeholder。
  const weekStatus: WeekStatus | undefined = data?.week_status;
  const weekReady = isWeekDataReady(weekStatus);
  const rows = weekReady ? (data?.rows ?? []) : [];
  const usingMock = !data && !isLoading;

  const columns: ColumnsType<ModelRow> = [
    {
      title: '品牌 / 车型',
      key: 'name',
      width: 200,
      render: (_, r) => (
        <div className="flex flex-col">
          <Link to={`/models/${r.model_id}`} className="text-slate-900 font-medium hover:underline">
            {r.model_name}
          </Link>
          <span className="text-[11px] text-slate-400">{r.brand_id}</span>
        </div>
      ),
    },
    {
      title: '综合评分',
      key: 'avg',
      width: 90,
      sorter: (a, b) => (a.metrics?.avg_score ?? 0) - (b.metrics?.avg_score ?? 0),
      render: (_, r) => <ScoreCell value={r.metrics?.avg_score} />,
    },
    {
      title: '评论数',
      key: 'rc',
      width: 80,
      render: (_, r) => <span>{r.metrics?.review_count ?? 0}</span>,
    },
    {
      title: '本周投诉',
      key: 'cc',
      width: 90,
      sorter: (a, b) => (a.metrics?.complaint_count ?? 0) - (b.metrics?.complaint_count ?? 0),
      render: (_, r) => <span>{r.metrics?.complaint_count ?? 0}</span>,
    },
    { title: '三电', key: 'b', width: 70, render: (_, r) => <ScoreCell value={r.metrics?.battery_score} /> },
    { title: '座舱', key: 'c', width: 70, render: (_, r) => <ScoreCell value={r.metrics?.cabin_score} /> },
    { title: '驾驶', key: 'd', width: 70, render: (_, r) => <ScoreCell value={r.metrics?.driving_score} /> },
    { title: '舒适', key: 'cf', width: 70, render: (_, r) => <ScoreCell value={r.metrics?.comfort_score} /> },
    { title: '服务', key: 'sv', width: 70, render: (_, r) => <ScoreCell value={r.metrics?.service_score} /> },
    { title: '智驾', key: 'sd', width: 70, render: (_, r) => <ScoreCell value={r.metrics?.smart_driving_score} /> },
    {
      title: '周变化',
      key: 'wow',
      width: 90,
      render: (_, r) =>
        r.metrics?.complaint_count_wow !== null && r.metrics?.complaint_count_wow !== undefined ? (
          <TrendArrow value={r.metrics.complaint_count_wow} />
        ) : (
          '—'
        ),
    },
    {
      title: '风险',
      key: 'risk',
      width: 90,
      render: (_, r) => (r.metrics ? <RiskTag level={r.metrics.risk_level} /> : '—'),
    },
  ];

  return (
    <div className="space-y-5">
      {usingMock && <MockBanner message="车型 weekly 接口暂不可用 — 显示空表" />}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[17px] lg:text-[20px] font-semibold text-slate-900 m-0">车型评分列表 · {week}</h1>
          <p className="text-[12px] lg:text-[13px] text-slate-500 mt-1 mb-0">
            {weekReady
              ? `共 ${rows.length} 款车型，6 维评分 + 投诉环比 + 风险等级。`
              : DATA_NOT_READY_HINT}
          </p>
        </div>
      </div>

      <Panel title="筛选">
        <div className="flex flex-col gap-3">
          <ChipFilter
            label="品牌"
            value={brandId}
            onChange={setBrandId}
            options={BRAND_OPTIONS}
          />
          <ChipFilter
            label="动力"
            value={powertrain}
            onChange={setPowertrain}
            options={POWERTRAIN_OPTIONS}
          />
        </div>
      </Panel>

      <Panel title="车型评分" noPadding>
        {!weekReady ? (
          <div className="text-[12px] text-slate-400 py-12 text-center">{DATA_NOT_READY_HINT}</div>
        ) : (
        <div className="p-4">
          <DataTable<ModelRow>
            columns={columns}
            data={rows}
            rowKey="model_id"
            loading={isLoading}
            pageSize={30}
            mobileMode="card"
            renderMobileCard={(r) => (
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Link
                    to={`/models/${r.model_id}`}
                    className="text-slate-900 font-medium hover:underline text-[14px]"
                  >
                    {r.model_name}
                  </Link>
                  {r.metrics ? <RiskTag level={r.metrics.risk_level} /> : <span className="text-slate-400">—</span>}
                </div>
                <div className="text-[11px] text-slate-400">{r.brand_id}</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-slate-600">
                  <span>综合 <ScoreCell value={r.metrics?.avg_score} /></span>
                  <span>评论 {r.metrics?.review_count ?? 0}</span>
                  <span>投诉 {r.metrics?.complaint_count ?? 0}</span>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
                  <span>三电 <ScoreCell value={r.metrics?.battery_score} /></span>
                  <span>座舱 <ScoreCell value={r.metrics?.cabin_score} /></span>
                  <span>驾驶 <ScoreCell value={r.metrics?.driving_score} /></span>
                  <span>舒适 <ScoreCell value={r.metrics?.comfort_score} /></span>
                  <span>服务 <ScoreCell value={r.metrics?.service_score} /></span>
                  <span>智驾 <ScoreCell value={r.metrics?.smart_driving_score} /></span>
                </div>
              </div>
            )}
          />
        </div>
        )}
      </Panel>
    </div>
  );
}
